import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { showToast } from "services/utils/Status";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { useFetch } from "services/hooks/useFetch";
import { themes, scrollTop } from "services/constants";
import { useSelector } from "react-redux";
import { Loader } from "components/Loader";
import { ShiftGradeHeader } from "pages/Administration/AdministrationHeaders";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { tableRowSize } from "services/constants";
import { CustomButton } from "components/CustomComponents";
import { CreateShiftGradePanel } from "./CreateShiftGradePanel";
import { ViewShiftGradePanel } from "./ViewShiftGrade/ViewShiftGradePanel";
import { tooltipStyleProps } from "services/constants/Styles";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { Typography, Box, Paper, Drawer, Tooltip } from "@mui/material";
import { PeopleAlt } from "@mui/icons-material";

const keys = ["name", "shift_hours"];

export const ShiftGrade = ({ setSelectedShiftItem }) => {
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [shiftGradeDetails, setShiftGradeDetails] = useState();
  const [editShiftGradeDetails, setEditShiftGradeDetails] = useState("");
  const containerRef = useRef(null);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [handleLoader, setHandleLoader] = useState(true);
  const [getId, setGetId] = useState("");
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Shift",
    "Shift Grade"
  );
  const {
    result: shiftGradeData,
    setLoad: reCallApi,
    loading,
  } = useFetch(`${domain}administration/shiftroster/shiftgrade/shift-grades/`);

  let sortedShiftData = shiftGradeData.sort((a, b) => {
    const durationA = a.shift_duration || "00:00";
    const durationB = b.shift_duration || "00:00";
    return durationA.localeCompare(durationB);
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (sortedShiftData?.length !== 0 && sortedShiftData) {
      const item = sortedShiftData.find((d) => d.id === getId);
      setEditShiftGradeDetails(item);
    }
  }, [getId, sortedShiftData]);

  const handleCreateShift = () => {
    setIsCreatePanelOpen(true);
  };

  const handleViewShift = () => {
    setIsViewPanelOpen(true);
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setShiftGradeDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  return handleLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Shift Grade
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define week-off rules and shift hours
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2.5 }}>
            <Tooltip
              title="Move to Shift Grade Assignment"
              arrow
              slotProps={tooltipStyleProps}
            >
              <Mui.IconButton
                size="small"
                sx={{
                  bgcolor: themes?.whiteColor,
                  borderRadius: 1,
                  border: `1px solid ${themes?.mutedgreen}`,
                  width: 34,
                  height: 34,
                  mt: 0.5,
                }}
                onClick={() => setSelectedShiftItem("Employee Shift Grade")}
              >
                <PeopleAlt
                  sx={{ fontSize: 18, color: themes?.softMutedGreen }}
                />
              </Mui.IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Shift Grade Records"
            filterHeaders={ShiftGradeHeader}
            data={sortedShiftData}
            filteredData={shiftGradeDetails}
            searchKeys={keys}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={
              <CustomButton
                actionFuntion={() => {
                  if (check_access) {
                    handleCreateShift();
                  } else {
                    showToast(
                      "error",
                      `Access denied: You are not authorized to create shift grade`
                    );
                  }
                }}
                actionName="New"
                typeName="button"
              />
            }
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                    top: -1,
                    zIndex: 10,
                  }}
                >
                  <Mui.TableRow sx={tableRowSize}>
                    {ShiftGradeHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {shiftGradeDetails?.length === 0 ? (
                    <InfoMessage dataLength={sortedShiftData} />
                  ) : (
                    shiftGradeDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {ShiftGradeHeader.map((head) =>
                            head.name === "Break Allowed" ? (
                              <Mui.TableCell>
                                {value?.break_allowed ? "Yes" : "No"}
                              </Mui.TableCell>
                            ) : (
                              <Mui.TableCell
                                onClick={() => {
                                  handleViewShift();
                                  setEditShiftGradeDetails(value);
                                }}
                              >
                                {value[head.key] ? value[head.key] : "0"}
                              </Mui.TableCell>
                            )
                          )}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {shiftGradeDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={shiftGradeDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          isCreatePanelOpen ? handleCreateClosePanel() : handleViewClosePanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.themes?.whiteColor,
          },
        }}
      >
        {isCreatePanelOpen ? (
          <CreateShiftGradePanel
            onClose={handleCreateClosePanel}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
          />
        ) : isViewPanelOpen ? (
          <ViewShiftGradePanel
            onClose={handleViewClosePanel}
            editShiftGradeDetails={editShiftGradeDetails}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            setGetId={setGetId}
            setIsViewPanelOpen={setIsViewPanelOpen}
            screenAccess={check_access}
          />
        ) : null}
      </Drawer>
    </React.Fragment>
  );
};
